import { hoyISO } from './theme';

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';
const CLAVE_TOKEN = 'wms-zf-token';
/** Cabecera con la que el servidor devuelve la sesión renovada. */
const CABECERA_SESION = 'X-Sesion-Token';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

let token: string | null = null;
try {
  token = localStorage.getItem(CLAVE_TOKEN);
} catch {
  token = null;
}

export const tokenActual = () => token;

export function guardarToken(nuevo: string | null): void {
  token = nuevo;
  try {
    if (nuevo) localStorage.setItem(CLAVE_TOKEN, nuevo);
    else localStorage.removeItem(CLAVE_TOKEN);
  } catch {
    /* modo privado: la sesión vive sólo en memoria */
  }
}

async function pedir<T>(ruta: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json');

  let res: Response;
  try {
    res = await fetch(`${BASE}${ruta}`, { ...init, headers });
  } catch {
    throw new ApiError('No pudimos contactar al servidor del depósito.', 0);
  }

  // Sesión deslizante: si el servidor la renovó, se guarda la nueva y el
  // operario nunca llega a ver la pantalla de ingreso.
  const renovada = res.headers.get(CABECERA_SESION);
  if (renovada) guardarToken(renovada);

  if (res.status === 401) {
    guardarToken(null);
    throw new ApiError('Tu sesión venció. Volvé a iniciar sesión.', 401);
  }

  const texto = await res.text();
  const datos = texto ? (JSON.parse(texto) as unknown) : null;

  if (!res.ok) {
    const mensaje =
      datos && typeof datos === 'object' && 'error' in datos
        ? String((datos as { error: unknown }).error)
        : `Error ${res.status}`;
    throw new ApiError(mensaje, res.status);
  }

  return datos as T;
}

/**
 * Escucha `GET /eventos` (SSE): `onCambio` corre cada vez que alguien guarda
 * algo en el servidor. Con `fetch` y no con `EventSource` porque éste no deja
 * mandar la cabecera de la sesión. Si se corta, reintenta solo cada 5 s.
 * Devuelve la función para dejar de escuchar.
 */
export function escucharCambios(onCambio: () => void): () => void {
  const corte = new AbortController();
  let reconexion = false;
  void (async () => {
    while (!corte.signal.aborted) {
      try {
        if (token) {
          const res = await fetch(`${BASE}/eventos`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: corte.signal,
          });
          if (res.ok && res.body) {
            // Lo que pasó mientras estuvo cortado no llegó: se refresca igual.
            if (reconexion) onCambio();
            reconexion = true;
            const lector = res.body.pipeThrough(new TextDecoderStream()).getReader();
            for (;;) {
              const { value, done } = await lector.read();
              if (done) break;
              if (value.includes('data:')) onCambio();
            }
          }
        }
      } catch {
        /* red caída o abortado: el while decide */
      }
      await new Promise((r) => setTimeout(r, 5_000));
    }
  })();
  return () => corte.abort();
}

/**
 * Baja un archivo pasando por fetch, porque la sesión va en una cabecera y un
 * `<a href>` directo no la manda.
 */
async function descargar(ruta: string, nombre: string, mensajeError: string): Promise<void> {
  const headers = new Headers();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(`${BASE}${ruta}`, { headers });
  if (!res.ok) throw new ApiError(mensajeError, res.status);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const json = (body: unknown) => ({ body: JSON.stringify(body) });

// ------------------------------------------------------------------- tipos

export type Rol = 'admin' | 'operador' | 'operador_zf';

export type Perfil = {
  nombre: string;
  mail: string;
  rol: Rol;
  rolEtiqueta: string;
  /** Vistas que este rol puede abrir; el servidor valida igual cada ruta. */
  vistas: string[];
  vistaInicial: string;
  foto: string;
};

export type UsuarioAdmin = {
  /** null cuando todavía no tiene registro: un admin de ADMIN_EMAILS sin ingresar. */
  id: number | null;
  nombre: string;
  mail: string;
  rol: Rol;
  rolEtiqueta: string;
  /** Admin fijado por configuración: no se le puede cambiar el rol ni borrar. */
  fijo: boolean;
  invitadoPor: string | null;
  ultimoIngreso: string | null;
  nuncaEntro: boolean;
  /** Se le quitó el acceso. Sigue en la lista para poder devolvérselo. */
  bloqueado: boolean;
};

export type Usuarios = {
  roles: { id: Rol; etiqueta: string; descripcion: string }[];
  adminsFijos: string[];
  /** Dominios cuyas cuentas se dan de alta solas. Sólo lo ve un admin. */
  dominios: string[];
  total: number;
  porRol: { rol: Rol; etiqueta: string; cantidad: number }[];
  usuarios: UsuarioAdmin[];
};

export type ConfigAuth = {
  /** Client ID de Google; null si el servidor no lo tiene configurado. */
  googleClientId: string | null;
  /** Atajo de desarrollo para entrar sin Google. */
  loginDemo: boolean;
};

// ------------------------------------------------------------------ etiquetas

export type ElementoEtiqueta = {
  id: string;
  tipo: 'texto' | 'barras' | 'qr' | 'linea' | 'rectangulo' | 'logo';
  /** Todo en milímetros, con el origen arriba a la izquierda. */
  x: number;
  y: number;
  ancho: number;
  alto: number;
  giro: number;
  /** Texto fijo, con variables entre llaves dobles. */
  contenido?: string;
  cuerpo?: number;
  negrita?: boolean;
  alineacion?: 'izquierda' | 'centro' | 'derecha';
  mostrarTexto?: boolean;
  correccion?: string;
  grosor?: number;
  relleno?: boolean;
  /** Los elementos de un mismo grupo se eligen y se mueven juntos. */
  grupo?: string | null;
};

export type PlantillaEtiqueta = { version: number; elementos: ElementoEtiqueta[] };

/**
 * Cómo se dibuja respecto del papel. El rollo no cambia: apaisada se diseña a
 * lo largo y al imprimir se gira un cuarto de vuelta.
 */
export type Orientacion = 'vertical' | 'apaisada';

/** Para qué es la etiqueta: no lleva lo mismo la del pallet que la del capacho. */
export type UsoEtiqueta = 'pallet' | 'capacho';

/** El área sobre la que se dibuja, dadas las medidas del papel. */
export const areaDeDibujo = (ancho: number, alto: number, orientacion: Orientacion) =>
  orientacion === 'apaisada' ? { ancho: alto, alto: ancho } : { ancho, alto };

export type EtiquetaPlantilla = {
  id: number;
  nombre: string;
  descripcion: string;
  ancho: number;
  alto: number;
  dpi: number;
  uso: UsoEtiqueta;
  orientacion: Orientacion;
  plantilla: PlantillaEtiqueta;
  activa: boolean;
  predeterminada: boolean;
  version: number;
  creadaPor: string | null;
  actualizadaPor: string | null;
  actualizadaEn: string;
};

/**
 * El maestro de materiales.
 *
 * `codigo` es el código del producto (1721423040) y `descripcion` es el
 * nombre del material (TUBE). `detalle` es la descripción larga que trae la
 * planilla del cliente: se guarda, no se muestra.
 */
export type CatalogoArticulos = {
  articulos: {
    codigo: string;
    descripcion: string;
    detalle?: string;
    proveedor: string;
    /**
     * Cuántas piezas entran como máximo en un capacho de este material. Es lo
     * que parte una orden de trabajo: ZF pide un total y el sistema lo corta
     * de a esto. `null` es «no se cargó», y entonces la orden entra en un solo
     * capacho y la parte el depósito.
     */
    maxPorCapacho: number | null;
    pallets: number;
    piezas: number;
  }[];
  /** Los proveedores ya usados, para elegir en vez de volver a escribirlos. */
  proveedores: string[];
  sinProveedor: number;
  /** Cuántos no tienen cargado el máximo por capacho. */
  sinMaximo: number;
};

/** La previa de una carga de productos: qué entra y qué se actualiza. */
export type ImportacionProductos = {
  confirmado: boolean;
  resumen: {
    filas: number;
    validos: number;
    conProblemas: number;
    nuevos: number;
    actualizados: number;
    /** Los que quedan llamándose como su código, porque la planilla no trajo nombre. */
    sinNombre: number;
    /** Los que entran sin piezas por capacho: sus órdenes no se reparten solas. */
    sinMaximo: number;
  };
  problemas: { linea: number; codigo: string; motivo: string }[];
  muestra?: {
    codigo: string;
    nombre: string;
    detalle: string | null;
    proveedor: string | null;
    maxPorCapacho: number | null;
    nuevo: boolean;
  }[];
};

/**
 * Lo que se puede vaciar y qué se lleva cada cosa.
 *
 * `arrastra` es la parte que importa mostrar: vaciar el maestro de artículos se
 * lleva el stock, las órdenes de trabajo y las líneas de recepción, y eso no se
 * adivina apretando el botón.
 */
export type Limpieza = {
  confirmacion: string;
  objetivos: { id: string; nombre: string; detalle: string; filas: number; arrastra: string[] }[];
};

export type ProveedorManual = { id: number; nombre: string; numero: string };

/** Un material del maestro (`articulos`), con el proveedor que lo provee. */
export type MaterialManual = { codigo: string; nombre: string; proveedorId: number | null };

/**
 * El catálogo con el que se completa una etiqueta a mano.
 *
 * Los proveedores y los materiales son del depósito y se eligen de una lista.
 * El resto —la factura, el documento de recepción— son de un ingreso: de esos
 * sólo se ofrece lo último que se usó, por clave de variable.
 */
export type CatalogoManual = {
  proveedores: ProveedorManual[];
  materiales: MaterialManual[];
  recientes: Record<string, string[]>;
};

export type CatalogoEtiquetas = {
  impresora: { nombre: string; dpi: number; anchoMm: { min: number; max: number }; altoMm: { min: number; max: number } };
  tamanos: { id: string; nombre: string; ancho: number; alto: number }[];
  orientaciones: Orientacion[];
  usos: { id: UsoEtiqueta; nombre: string; detalle: string }[];
  tipos: string[];
  /** Las variables que tienen sentido para cada tipo de etiqueta. */
  variables: Record<
    UsoEtiqueta,
    {
      grupo: string;
      /** El grupo no lo sabe el sistema: se tipea al imprimir. */
      pedidas?: boolean;
      /** La que se puede dejar en blanco y emitir la etiqueta igual. */
      variables: { clave: string; nombre: string; muestra: string; opcional?: boolean }[];
    }[]
  >;
  /** Los campos de la carga manual, para el formulario de etiqueta a mano. */
  manuales: { clave: string; nombre: string }[];
  muestra: Record<string, string>;
};

export type Dashboard = {
  palletsEnDeposito: number;
  sectores: number;
  /** Niveles de rack existentes. Cero mientras no se cargue la planta. */
  posiciones: number;
  ocupacion: number;
  contenedoresEnTransito: number;
  proximoArribo: string | null;
  otAbiertas: number;
  otEnArmado: number;
  /** Pedidos por mail sin resolver. */
  pedidosMail: number;
  palletsEnFraccionamiento: number;
  palletsSinUbicar: number;
  palletsSegundaQuincena: number;
  sincronizado: string;
};

export type Ajustes = {
  /** Si Stock disponible muestra el botón que saca de su posición a los pallets enteros. */
  sacarEnteros: boolean;
  /** Etiquetas que le quedan al rollo de la térmica (100 × 150). null, sin contador. */
  rolloEtiquetas: number | null;
  cambiadaPor: string | null;
  cambiadaEn: string | null;
};

export type PalletStock = {
  /** Lo que queda sin comprometer: `cantidad` menos los capachos aceptados sin entregar. */
  remanente: number;
  id: string;
  /** El artículo que más pesa; con varios lleva "+N" al final. */
  articulo: string;
  /** Cuántas líneas tiene el pallet. Más de una es un pallet mezclado. */
  articulos: number;
  parte: string;
  lote: string;
  cantidad: number;
  ubicacion: string;
  /** Nivel del rack, null si todavía no tiene posición asignada. */
  nivel: number | null;
  /** false para lo recién ingresado, que todavía no tiene posición asignada. */
  ubicado: boolean;
  estado: string;
  /** Si sale entero o se fracciona. Decidido al armarlo, no cambia. */
  tipo: TipoPallet;
  /** De qué contenedor bajó. Null es "no se cargó", no "no vino de ninguno". */
  contenedor: string | null;
};

export type Stock = { total: number; estados: string[]; pallets: PalletStock[] };

export type ArticuloDisponible = {
  codigo: string;
  descripcion: string;
  /** "ART-10233 · Perfil aluminio", como se muestra en toda la app. */
  etiqueta: string;
  /**
   * De qué clase es el stock disponible. Un artículo que tiene de las dos
   * viene dos veces: lo que se fracciona no sirve para un pedido de pallet
   * entero, y al revés tampoco.
   */
  tipo: TipoPallet;
  /**
   * Piezas por capacho de este material: con eso la solicitud muestra en
   * cuántos capachos se va a partir el total antes de mandarlo. `null` es que
   * no está cargado, y entonces sale un capacho solo.
   */
  maxPorCapacho: number | null;
  pallets: number;
  piezas: number;
};

/**
 * El remito de salida: el papel que acompaña la mercadería al camión.
 *
 * Se arma eligiendo órdenes de trabajo ya despachadas y sale impreso por
 * triplicado. Los datos del cliente vienen del servidor —hoy ZF y sólo ZF— y
 * no se editan desde acá.
 */
export type DatosCliente = {
  nombre: string;
  domicilio: string;
  cuit: string;
  telefono: string;
  condicionIva: string;
};

export type LineaRemito = {
  /** Las órdenes del renglón: dos del mismo producto van en uno, sumadas. Vacío es escrito a mano. */
  ordenes: string[];
  codigo: string;
  producto: string;
  cantidad: number;
  /** Con qué se escribió el renglón (sólo el que propone una orden): para reescribirlo al sumar otra. */
  origen?: OrdenParaRemito;
};

/** Espejo de `OrdenParaRemito` en `backend/src/domain/remito.ts`. */
export type OrdenParaRemito = {
  id: string;
  articulo: string;
  descripcion: string | null;
  capachos: number;
  piezasTotal: number | null;
  unidad: string;
};

/**
 * Una orden que se puede meter en un remito, con un renglón ya propuesto por
 * cada artículo: se elige la orden entera y entran todos.
 *
 * El servidor sólo manda las que ya tienen mercadería entregada y todavía no
 * viajaron en ningún remito: una que ya salió no vuelve a la lista. Reemitir el
 * papel se hace desde el historial, que reimprime el remito que se emitió.
 */
export type OrdenRemitible = {
  ordenId: string;
  lineas: LineaRemito[];
  /** Los artículos de la orden, con su nombre. */
  articulos: string[];
  salidas: number;
  entregadas: number;
  /** Las entregadas que ya viajaron en otro remito: los renglones son el resto. */
  remitidas: number;
  creadaEn: string;
  /** Las observaciones de la orden: se sugieren en las del remito. */
  observaciones: string;
};

export type Transporte = { nombre: string; cuit: string; domicilio: string };

export type NuevoRemito = {
  cliente: DatosCliente;
  /** El número que saldría si se emitiera ahora: «0009-00001235». Referencia, no reserva. */
  siguiente: string;
  /** Con el que salen los remitos nuevos: «0009». */
  puntoDeVenta: string;
  ordenes: OrdenRemitible[];
  transportes: Transporte[];
};

/** Un remito emitido, como se ve en la lista. */
export type RemitoEnLista = {
  id: string;
  fecha: string;
  transporte: string;
  facturas: string;
  emitidoPor: string | null;
  /** El número se escribió a mano en vez de salir de la secuencia. */
  numeroManual: boolean;
  lineas: number;
  /** Cuántas veces se corrigió después de emitirlo. */
  ediciones: number;
  creadoEn: string;
  /** Dado de baja: sigue guardado, pero ya no vale. */
  anuladoEn: string | null;
  anuladoPor: string | null;
};

/** Un registro del historial de un remito: la emisión o una corrección. */
export type CambioDeRemito = { en: string; por: string | null; detalle: string };

/** Un remito entero, que es lo que se imprime. */
export type Remito = {
  id: string;
  fecha: string;
  transporte: string;
  transporteCuit: string;
  transporteDomicilio: string;
  observaciones: string;
  facturas: string;
  emitidoPor: string | null;
  /** Dado de baja. Sólo viene del servidor. */
  anuladoEn?: string | null;
  cliente: DatosCliente;
  lineas: LineaRemito[];
  /** Lo que le pasó, de lo último a lo primero. Sólo viene del servidor. */
  cambios?: CambioDeRemito[];
};

export type Contenedor = {
  id: string;
  fecha: string;
  pallets: number;
  /** Pallets que ya salieron del contenedor: ubicados o despachados. */
  procesados: number;
  estado: string;
  muelle: string | null;
  factura: string;
  docRecepcion: string;
  /** El Nº de lote del ingreso, que no es el FIFO. */
  lote: string;
  /** El orden de llegada: 1 es el más viejo, y sus pallets se sugieren primero. */
  numero: number | null;
  /** `YYYY-MM-DD`, para editarla. */
  fechaIso: string;
};

/** Todo lo de un contenedor en una respuesta (`/contenedores/:id/ficha`). */
export type FichaContenedor = {
  datos: Contenedor;
  resumen: {
    pallets: number;
    enRack: number;
    enZonaDePiso: number;
    sinUbicar: number;
    despachados: number;
    completos: number;
    fraccionamiento: number;
    piezasIniciales: number;
    piezasArriba: number;
    piezasEntregadas: number;
  };
  porArticulo: { articulo: string; etiqueta: string; pallets: number; piezas: number; arriba: number; entregadas: number }[];
  pallets: {
    id: string;
    tipo: TipoPallet;
    estado: string;
    /** El artículo con más piezas, con su descripción. */
    articulo: string;
    articulos: number;
    items: { articulo: string; descripcion: string | null; numeroParte: string | null; lote: string | null; piezas: number; entregadas: number }[];
    piezas: number;
    arriba: number;
    posicion: string | null;
    nivel: number | null;
    /** «A1-04 · N2», «P1 · zona de piso» o null sin ubicación. */
    ubicacion: string | null;
    fechaIngreso: string;
  }[];
  /** La recepción, sin los adjuntos (van en `documentos`). Sin líneas si no hubo borrador. */
  recepcion: Omit<Recepcion, 'adjuntos'>;
  salidas: {
    id: string;
    orden: string | null;
    articulo: string;
    unidad: Unidad;
    piezas: number;
    estado: string;
    pallet: string;
    armadoEn: string | null;
    entregadoEn: string | null;
  }[];
  documentos: Adjunto[];
  eliminados: PalletEliminado[];
};

/** Un artículo de la orden y cuánto se pidió de él. */
export type RenglonOrden = {
  articulo: string;
  /** Capachos, o pallets en una orden de pallets completos. */
  capachos: number;
  piezasCapacho: number;
  /** Null en pallets completos: las piezas las trae cada pallet. */
  totalPiezas: number | null;
  detalle: string;
};

/** Una orden de trabajo: un pedido de ZF, con uno o varios artículos. */
export type Orden = {
  id: string;
  /** La unidad es de la orden entera: capachos o pallets completos. */
  unidad: Unidad;
  lineas: RenglonOrden[];
  articulos: string[];
  /** Null en una orden de pallets completos: las piezas las trae cada pallet. */
  totalPiezas: number | null;
  /** La orden en una línea: el detalle del artículo, o cuántos son y el total. */
  detalle: string;
  /**
   * Pendiente → En armado → Despachada. Sale de sus salidas y no de una
   * columna: una orden está despachada cuando se fue todo lo que pidió.
   */
  estado: string;
  /** Cuántas salidas tiene la orden y cuántas ya se fueron del depósito. */
  salidas: number;
  entregadas: number;
  /** Se cerró con lo que había: qué no salió. Null si salió todo. */
  faltante: string | null;
  /** El mail de quien la pidió. El cliente sólo recibe las suyas. */
  pedidaPor: string | null;
  observaciones: string | null;
  /** `YYYY-MM-DD`, o null si quien pidió no puso ninguna. */
  entregaEstimada: string | null;
  creadaEn: string;
};

export type LineaRecepcion = {
  id: number;
  pallet: string;
  articulo: string;
  teorico: number;
  fisico: number;
  diferencia: number;
  estado: string;
  /** La etiqueta fue leída: el pallet bajó del contenedor. */
  leido: boolean;
  leidoEn: string | null;
  leidoPor: string | null;
};

export type Adjunto = { id: number; nombre: string; peso: string };

/** Un pallet candidato a la etiqueta de pallet (`/etiquetas-pallet` del backend). */
export type PalletEtiqueta = {
  id: string;
  /** El artículo con más piezas, o null si el pallet no tiene ninguna línea. */
  articulo: string | null;
  /** Cuántos artículos distintos tiene arriba: más de uno se muestra «+N». */
  articulos: number;
  /** Piezas que le quedan arriba (las del borrador, si todavía no se confirmó). Cero es un pallet vacío. */
  piezas: number;
  contenedor: string | null;
  /** El número de llegada del contenedor (`contenedores.numero`): lo que va en la etiqueta. */
  contenedorNumero: number | null;
};

export type Recepcion = {
  contenedor: string;
  estado: string;
  muelle: string | null;
  confirmado: boolean;
  borradorEn: string | null;
  confirmadoEn: string | null;
  /** Pallets declarados en el borrador. */
  pallets: number;
  leidos: number;
  sinLeer: number;
  diferencias: number;
  piezasEsperadas: number;
  piezasLeidas: number;
  lineas: LineaRecepcion[];
  adjuntos: Adjunto[];
};

export type Lectura = {
  ok: boolean;
  mensaje: string;
  /** true cuando la etiqueta ya se había leído antes. */
  yaLeido?: boolean;
  lineaId?: number;
  pallet?: string;
  resumen?: Recepcion;
};

/**
 * Pedido → Aceptado → Entregado. Se avanza y se vuelve de a un paso; saltear
 * no se puede. La regla vive en el backend (`domain/capachos.ts`), acá está el
 * orden para poder dibujar los botones.
 */
export const ESTADOS_CAPACHO = ['Pedido', 'Aceptado', 'Entregado'] as const;

/**
 * Qué sale del depósito. Un capacho se arma a mano con parte de un pallet; un
 * pallet completo sale entero, que es lo que ZF pide de los pallets que no se
 * fraccionan. Las dos cosas recorren los mismos tres estados de acá arriba.
 */
export const UNIDADES = ['capacho', 'pallet'] as const;

export type Unidad = (typeof UNIDADES)[number];

/**
 * De qué clase es un pallet, decidido al armarlo y fijo desde entonces: el de
 * fraccionamiento se abre en la mesa, el completo sale entero.
 */
export const TIPOS_PALLET = ['fraccionamiento', 'completo'] as const;

export type TipoPallet = (typeof TIPOS_PALLET)[number];

export type EstadoCapacho = (typeof ESTADOS_CAPACHO)[number];

export type Capacho = {
  id: string;
  piezas: number;
  etiquetado: boolean;
  /** De qué línea del pallet salió. Null en capachos anteriores a las líneas. */
  itemId: number | null;
  articulo: string;
  estado: EstadoCapacho;
  unidad: Unidad;
  /** La OT que lo pidió. Null en los anteriores a las órdenes. */
  orden: string | null;
  /** Cuándo y quién lo armó en la estación de fraccionamiento. Null sin armar. */
  armadoEn: string | null;
  armadoPor: string | null;
  /** El capacho de la etiqueta, si esta fila es la parte que lo completa con otro pallet. */
  parteDe: string | null;
};

/** Una línea del pallet: un artículo de los que tiene arriba, con su saldo. */
export type LineaDePallet = {
  id: number;
  articulo: string;
  descripcion: string;
  lote: string | null;
  numeroParte?: string | null;
  piezas: number;
  usado: number;
  /** Lo que queda libre para meter en un capacho nuevo. */
  saldo: number;
  /** Lo que RDM todavía tiene guardado y lo que ya pasó a poder de ZF. */
  stockRdm?: number;
  stockZf?: number;
  /** Piezas por capacho del maestro (`articulos.max_por_capacho`). Null es "no cargado". */
  maxPorCapacho?: number | null;
};

/** Un capacho que ZF pidió y todavía no tiene pallet asignado. */
export type CapachoPedido = {
  id: string;
  orden: string | null;
  articulo: string;
  descripcion: string;
  piezas: number;
  unidad: Unidad;
  pedidoPor: string | null;
  pedidoEn: string;
  /**
   * De dónde puede salir. Para un capacho son los pallets con saldo suficiente
   * de ese artículo; para un pallet completo, los pallets enteros libres, en
   * orden FIFO, y ahí viene también el `item` con el que se acepta sin abrir
   * nada. Vacío es "no hay de dónde sacarlo".
   *
   * `ubicacion` es adónde ir a buscarlo —«A1-04 · N2»—, que es lo que el
   * operario necesita: el código del pallet no dice dónde está. Va en null
   * cuando el pallet todavía no está ubicado.
   */
  /**
   * De dónde puede salir el pedido, en el orden en que conviene sacarlo. El
   * primero es el recomendado: con `abierto`, el pallet que ya está en la mesa
   * y al que le alcanza el saldo.
   */
  candidatos?: {
    pallet: string;
    saldo: number;
    item?: number;
    ubicacion?: string | null;
    abierto?: boolean;
  }[];
};

/** Una orden como la mira la mesa: con cuántas salidas tiene en cada estado. */
export type OrdenEnMesa = {
  id: string;
  unidad: Unidad;
  /** Un renglón por artículo, dicho como lo pidió ZF. */
  lineas: string[];
  estado: string;
  pedidaPor: string | null;
  pedidaEn: string;
  /** `YYYY-MM-DD`, o null. */
  entregaEstimada: string | null;
  observaciones: string | null;
  salidas: number;
  pedidos: number;
  aceptados: number;
  entregados: number;
  /** Lo que no alcanza con el stock del depósito: «ART-1: se piden 300 pz y hay 120 libres». */
  faltantes: string[];
  /** Se cerró con lo que había: qué no salió. Null si salió todo. */
  faltante: string | null;
  /** Los remitos vigentes en que viajó: una orden puede ir en partes, un remito por cada una. */
  remitos: string[];
  /** Cuántas salidas entregadas todavía no viajaron en ningún remito. */
  porRemitir: number;
};

/** Una salida de la orden: un capacho o un pallet entero. */
export type SalidaDeOrden = {
  id: string;
  articulo: string;
  descripcion: string | null;
  unidad: Unidad;
  piezas: number;
  estado: EstadoCapacho;
  /** Null mientras está en «Pedido»: todavía no se decidió de dónde sale. */
  pallet: string | null;
  itemId: number | null;
  ubicacion: string | null;
  etiquetado: boolean;
  /** Sólo en «Pedido». El primero es el recomendado; `item` es la línea contra la que se acepta. */
  candidatos: { pallet: string; saldo: number; item: number; ubicacion: string | null; abierto?: boolean }[];
  /** Sólo en «Pedido»: capachos que quedaron armados de otra orden y entran en éste. */
  armados: { id: string; piezas: number }[];
  /** Cuándo y quién lo armó en la estación de fraccionamiento. Null sin armar. */
  armadoEn: string | null;
  armadoPor: string | null;
  /** El capacho de la etiqueta, si esta fila es la parte que lo completa con otro pallet. */
  parteDe: string | null;
};

export type OrdenDeFraccionamiento = PedidosDeEtiqueta & {
  orden: OrdenEnMesa;
  salidas: SalidaDeOrden[];
  grupos: GrupoDeEtiqueta[];
};

/**
 * Los capachos de una misma línea de pallet (pallet y producto), con los datos
 * de etiqueta que les tocan. Cada producto trae los papeles del contenedor del
 * que bajó, y en una orden pueden ser varios.
 */
export type GrupoDeEtiqueta = {
  /** El id de la línea del pallet. */
  clave: string;
  pallet: string;
  articulo: string;
  descripcion: string;
  contenedor: string | null;
  capachos: string[];
  cargados: Record<string, string>;
};

/** Los datos de etiqueta por grupo, con la `clave` del grupo. */
export type DatosPorGrupo = Record<string, Record<string, string>>;

export type Fraccionamiento = {
  pallet: string;
  tipo: TipoPallet;
  /** De qué contenedor bajó. Null es "no se cargó". */
  contenedor: string | null;
  articulo: string;
  codigoArticulo: string;
  lote: string | null;
  ubicacion: string;
  total: number;
  usado: number;
  saldo: number;
  /** El contenido real. El saldo que importa al armar un capacho es el de acá. */
  items: LineaDePallet[];
  /** Lo que ZF pidió de estos artículos y todavía nadie aceptó. */
  pedidos: CapachoPedido[];
  capachos: Capacho[];
  /** Cuántos capachos hay en cada estado, para el resumen de la pantalla. */
  porEstado: Record<EstadoCapacho, number>;
  grupos: GrupoDeEtiqueta[];
};

// ------------------------------------------------------------ resumen diario

/**
 * Lo que administración necesita ver de un día: qué entró, qué salió, cómo
 * quedó el stock y qué se movió.
 *
 * Ojo con la mezcla de tiempos, que es deliberada: `kpis` y `serie` son del
 * día pedido, pero `stock`, `capachos` y `ordenes.abiertas` son la foto de
 * **hoy**. Un capacho pendiente es pendiente ahora, no el martes pasado.
 */
export type Periodo = 'dia' | 'semana' | 'quincena' | 'mes' | 'anio' | 'todo';

/** Lo que se le cobra a ZF, en pesos. */
export type Tarifas = { cargaDescarga: number; movimientoZorra: number; retrabajo: number; almacenaje: number };

/**
 * Los cambios de tarifas, del más nuevo al más viejo. `vigenteDesde` null es
 * «desde siempre»; `rigeDesde` es desde cuándo regiría un cambio guardado hoy.
 */
export type HistorialTarifas = {
  rigeDesde: string;
  items: (Tarifas & { vigenteDesde: string | null; cargadoPor: string | null })[];
};

export type ResumenDiario = {
  periodo: Periodo;
  /** El día elegido; el período es el que lo contiene, de `desde` a `hasta`. */
  dia: string;
  desde: string;
  hasta: string;
  hoy: boolean;
  /**
   * Un día del período de al lado. Null en «todo», que no tiene vecinos, y
   * `anterior` también en el primer período con datos.
   */
  anterior: string | null;
  siguiente: string | null;
  kpis: {
    palletsIngresados: number;
    piezasIngresadas: number;
    capachosEntregados: number;
    /** Los que salieron enteros. No son capachos: es otra operación. */
    palletsDespachados: number;
    piezasEntregadas: number;
    movimientos: number;
    palletsEnDeposito: number;
  };
  stock: { rdm: number; zf: number; sinPaletizar: number; posicionesOcupadas: number; posicionesTotales: number };
  ingresos: { articulo: string; descripcion: string; pallets: number; piezas: number }[];
  egresos: {
    articulo: string;
    descripcion: string;
    /** De qué contenedor bajó el pallet del que salió. Null es "no se cargó". */
    contenedor: string | null;
    capachos: number;
    pallets: number;
    piezas: number;
  }[];
  capachos: { estado: EstadoCapacho; cantidad: number; piezas: number }[];
  movimientos: {
    hora: string;
    tipo: string;
    detalle: string;
    posicion: string;
    operario: string;
    icono: string;
    tono: string;
  }[];
  ordenes: { abiertas: number; creadasHoy: number; despachadasHoy: number };
  /**
   * Los puntos del gráfico, de viejo a nuevo: los 14 días que terminan en
   * `dia` en el día, los días del período hasta hoy, o los meses en el año y
   * en «todo» (ahí `dia` es 'AAAA-MM').
   */
  serie: { dia: string; piezasIngresadas: number; piezasEntregadas: number; movimientos: number }[];
  /** La cuenta para cobrarle a ZF. Sólo viene en la quincena. */
  facturacion: (Cuenta & { ocupacion: { dia: string; pallets: number }[] }) | null;
};

/** Lo que sale de `facturar` en el servidor, sea la quincena real o una prueba. */
export type Cuenta = {
  tarifas: Tarifas;
  pico: { dia: string; pallets: number };
  renglones: { concepto: string; detalle: string; cantidad: number; unitario: number; importe: number }[];
  total: number;
};

export type CantidadesFacturables = {
  palletsIngresados: number;
  capachosEntregados: number;
  palletsDespachados: number;
  picoPallets: number;
};

// ------------------------------------------- alta de contenedores y armado

/**
 * Lo que hace falta para dar de alta un contenedor: el maestro de artículos
 * para elegir qué trae, y los muelles para decir dónde descarga.
 */
export type NuevoContenedor = {
  estadoInicial: string;
  articulos: { codigo: string; descripcion: string; etiqueta: string }[];
  muelles: string[];
};

/**
 * Los papeles del ingreso que se cargan en el contenedor y llenan las
 * etiquetas de lo que bajó de él.
 */
export const PAPELES = [
  { campo: 'factura', nombre: 'Nº de factura', corto: 'factura', muestra: 'JZ20260522-01' },
  { campo: 'docRecepcion', nombre: 'Nº doc. de recepción', corto: 'doc.', muestra: 'PO 42086003' },
  { campo: 'lote', nombre: 'Nº de lote', corto: 'lote', muestra: '20260506' },
] as const;

export type Papeles = Partial<Record<(typeof PAPELES)[number]['campo'], string>>;

/** Un renglón de lo que el contenedor declara traer. */
export type LineaDeclarada = {
  articulo: string;
  /** El FIFO con el que entra. */
  lote: string | null;
  numeroParte: string | null;
  piezas: number;
  /** En cuántos pallets vienen: el alta los arma sola. Cero queda para Armado. */
  pallets: number;
  tipo: TipoPallet;
};

/** La pantalla de armado: lo que falta paletizar y los pallets sin ubicar. */
export type Armado = {
  /** Los contenedores cargados, para decir de cuál bajó el pallet. */
  contenedores: { id: string; fecha: string; estado: string }[];
  pendientes: {
    id: number;
    articulo: string;
    descripcion: string;
    lote: string | null;
    numeroParte: string | null;
    piezas: number;
    pendiente: number;
    /** De qué contenedor bajó. Null en lo cargado antes de que fueran la puerta. */
    contenedor: string | null;
    /** La clase con la que se declaró al cargar el contenedor. Null en los renglones viejos. */
    tipo: TipoPallet | null;
  }[];
  pallets: {
    id: string;
    tipo: TipoPallet;
    contenedor: string | null;
    ingreso: string;
    piezas: number;
    items: { id: number; articulo: string; descripcion: string; lote: string | null; piezas: number }[];
  }[];
};

/** Un capacho listo para etiquetar, con sus datos ya resueltos. */
export type Etiqueta = { id: string; texto: string; datos: Record<string, string> };

/**
 * Lo que hay que cargar a mano para poder emitir la etiqueta. Sale de la
 * plantilla en uso: si lleva el remito, se pide el remito.
 */
export type PedidosDeEtiqueta = {
  /** `deIngreso`: es un papel del contenedor (factura, doc. de recepción) y se pide por pallet y producto. */
  pedidas: { clave: string; nombre: string; opcional?: boolean; deIngreso?: boolean }[];
  /** Lo que ya se había cargado antes para este pallet. */
  cargados: Record<string, string>;
};

export type Hallazgo = {
  codigo: string;
  /**
   * `articulo` es lo que sale cuando lo leído no es un pallet sino el número
   * de parte del material, que es lo que trae impreso la etiqueta de ZF.
   */
  tipo: 'recepcion' | 'pallet' | 'capacho' | 'articulo' | 'ubicacion' | 'contenedor' | 'desconocido';
  titulo: string;
  detalle: string;
  contenedor?: string;
  lineaId?: number;
  estado?: string;
  ubicacion?: string | null;
  lote?: string | null;
  palletOrigen?: string;
  articulo?: string;
  /** Ubicación (`U-…`/`E-…`/`Z-…`) y pallet: dónde está. Null es sin ubicación; en una zona de piso, `nivel` es null. */
  posicion?: string | null;
  nivel?: number | null;
  niveles?: number;
  esEstacion?: boolean;
  esZona?: boolean;
  /** Sólo en `ubicacion`: qué pallet ocupa ese nivel. En una zona, null: ver `ocupantes`. */
  ocupante?: { pallet: string; articulo: string | null; tipo: TipoPallet } | null;
  /** Sólo en una zona de piso (`Z-…`): todos los pallets que tiene. */
  ocupantes?: { pallet: string; articulo: string | null; tipo: TipoPallet }[];
  /** Sólo en `pallet`. */
  tipoPallet?: TipoPallet;
  piezas?: number;
  /** La salida `DSP-…` aceptada que se lleva este pallet completo, si hay una. */
  salida?: { id: string; orden: string | null } | null;
  /** En `pallet` y `contenedor`: el número de orden de llegada del contenedor. */
  contenedorNumero?: number | null;
  /** Sólo en `capacho`. Las fechas son ISO. */
  orden?: string | null;
  unidad?: Unidad;
  armadoEn?: string | null;
  armadoPor?: string | null;
  entregadoEn?: string | null;
};

/** Un pallet que un admin eliminó del sistema, con lo que tenía arriba en ese momento. */
export type PalletEliminado = {
  id: number;
  pallet: string;
  contenedor: string | null;
  contenedorNumero: number | null;
  articulo: string | null;
  items: {
    articulo: string;
    descripcion: string | null;
    numeroParte: string | null;
    lote: string | null;
    piezas: number;
    stockRdm: number;
    stockZf: number;
    stockZfId: number | null;
  }[];
  piezas: number | null;
  tipo: string | null;
  estado: string | null;
  posicion: string | null;
  nivel: number | null;
  fechaIngreso: string | null;
  datos: Record<string, string>;
  motivo: string;
  eliminadoPor: string;
  /** ISO. */
  eliminadoEn: string;
  /** Sus piezas ya no esperan pallet: salieron también de lo declarado del contenedor. */
  declaradoQuitado: boolean;
  /** Se puede volver a poner en el sistema: le quedaba algo de RDM arriba (o era un pallet en cero). */
  restablecible: boolean;
};

/**
 * La vida de un pallet: cuándo entró, por dónde pasó y qué salió de él.
 *
 * Se arma con lo que ya está fechado —el ingreso, los movimientos de posición y
 * los tres momentos de cada salida—, así que es historia y no una bitácora
 * aparte que alguien tenga que acordarse de escribir.
 */
export type HistorialDePallet = {
  pallet: string;
  estado: string;
  tipo: TipoPallet;
  contenedor: string | null;
  ubicacion: string | null;
  eventos: {
    id: number;
    tipo: string;
    detalle: string;
    icono: string;
    tono: string;
    /** Quién lo hizo, cuando se sabe. */
    operario: string | null;
    /** En qué posición pasó, cuando el evento es de una. */
    posicion: string | null;
    fecha: string;
  }[];
};

export type NivelPosicion = {
  nivel: number;
  ocupado: boolean;
  pallet: string | null;
  articulo: string | null;
  piezas: number | null;
  /** El completo sale entero y no lleva conteo: no se le muestran piezas. */
  tipo: TipoPallet | null;
  ingreso: string | null;
  dias: number | null;
};

export type Posicion = {
  codigo: string;
  columna: number;
  /**
   * Huella en metros, **en coordenadas del predio**: el backend le aplica
   * `enElPredio` antes de mandarla (`mapa.ts:325`), así que ya tiene sumado el
   * rincón de su nave y **no hay que sumarle nada**. Ojo con la asimetría:
   * `Rack.geometria` sí sale cruda, en coordenadas de la nave, y ésta es la
   * única geometría del rack que está traducida. Null si el rack no fue ubicado.
   */
  caja: Huella | null;
  niveles: NivelPosicion[];
  /**
   * Sólo en una zona de piso: todos sus pallets, que no tienen nivel. Su
   * único nivel sale ocupado si hay alguno, y `ocupados` los cuenta.
   */
  zona?: Omit<NivelPosicion, 'nivel' | 'ocupado'>[];
  ocupados: number;
  hit: boolean;
};

/**
 * Dónde está un pallet, para leer: «A1-04 · N2», o «P1 · zona de piso» si no
 * tiene nivel (la zona de piso no tiene niveles). Igual que `textoUbicacion`
 * en `backend/src/lib/fmt.ts`.
 */
export const textoUbicacion = (posicion: string, nivel: number | null | undefined) =>
  nivel == null ? `${posicion.replace(/-\d+$/, '')} · zona de piso` : `${posicion} · N${nivel}`;

/**
 * Un rack común, la estación de fraccionamiento o una zona de piso: un área
 * libre del piso donde entran todos los pallets que haga falta, sin nivel.
 */
export type TipoRack = 'rack' | 'estacion' | 'piso';

/**
 * Geometría del rack en metros, tal como la guarda el editor de planta.
 *
 * **Está en coordenadas de la nave, no del predio**: el backend la manda cruda,
 * sin sumarle el rincón de su nave. Para ubicar un rack en el predio se usa
 * `posiciones[].caja`, que sí viene sumada.
 */
export type GeometriaRack = {
  xM: number | null;
  yM: number | null;
  anguloGrados: number;
  columnas: number;
  anchoPosicionM: number;
  profundidadM: number;
  niveles: number;
  altoNivelM: number;
};

export type Rack = {
  id: string;
  numero: number;
  columnas: number;
  niveles: number;
  tipo: TipoRack;
  geometria: GeometriaRack;
  posiciones: Posicion[];
};

export type Sector = {
  id: string;
  nombre: string;
  uso: string;
  color: string | null;
  /** Zona dibujada; sin ella, la caja que envuelve a sus racks. */
  area: Huella | null;
  dibujada: Huella | null;
  racks: Rack[];
  slots: number;
  llenos: number;
  pct: number;
  hits: number;
  nave: number;
};

export type PalletSinUbicar = {
  id: string;
  articulo: string;
  piezas: number;
  tipo: TipoPallet;
  /** null si el pallet no bajó de ningún contenedor. */
  contenedor: string | null;
  contenedorNumero: number | null;
};

/** Boca de carga: `caja` en null mientras no se la ubicó en el plano. */
export type MuelleMapa = { id: string; caja: Huella | null; nave: number };

/** Franja de piso ya ubicada: el mapa sólo devuelve las que están dibujadas. */
export type CaminoMapa = {
  id: number;
  nombre: string;
  tipo: string;
  caja: Huella;
  /** null cuando el camino está en el predio, fuera de las naves. */
  nave: number | null;
};

/** Punto en coordenadas del predio, en metros. */
export type Punto = { x: number; y: number };

/** El hueco de una abertura ya resuelto en dos puntos del muro que ocupa. */
export type AberturaMapa = {
  id: number;
  tipo: string;
  nombre: string;
  a: Punto;
  b: Punto;
  alto_m: number;
  antepecho_m: number;
};

/**
 * Un muro tal como lo entrega el mapa: perimetral o interior, ya resuelto en
 * tramos macizos -sin las aberturas- para que el cliente no tenga que restar
 * los huecos por su cuenta.
 */
export type MuroMapa = {
  clave: string;
  a: Punto;
  b: Punto;
  espesor_m: number;
  alto_m: number;
  material: string;
  /** El perimetral que comparten dos naves pegadas: sólo lo trae una de las dos. */
  compartido: boolean;
  tramos: { a: Punto; b: Punto }[];
  aberturas: AberturaMapa[];
};

/** Una nave en el plano: su caja ya está en coordenadas del predio. */
export type NaveMapa = { id: number; nombre: string; caja: Huella; alto_m: number; muros: MuroMapa[] };

export type Mapa = {
  hits: number;
  predio: { nombre: string; ancho: number; largo: number };
  naves: NaveMapa[];
  muelles: MuelleMapa[];
  caminos: CaminoMapa[];
  sectores: Sector[];
  /** Ya son stock, pero todavía no tienen lugar asignado. */
  sinUbicar: PalletSinUbicar[];
};

export type Movimiento = {
  id: number;
  tipo: string;
  detalle: string;
  icono: string;
  tono: string;
  operario: string;
  fecha: string;
};

export type Estadia = {
  pallet: string;
  articulo: string;
  ubicacion: string | null;
  ingreso: string;
  dias: number;
  /** En qué bloque de 15 días de permanencia va: 1, 2, 3… No es lo que se cobra. */
  posiciones: number;
  periodo: string;
};

export type Estadias = {
  items: Estadia[];
  primeraQuincena: number;
  segundaOMas: number;
};

export type Notificacion = {
  id: number;
  icono: string;
  tono: string;
  titulo: string;
  detalle: string;
  vista: string;
  leida: boolean;
  tiempo: string;
};

export type Notificaciones = { noLeidas: number; items: Notificacion[] };

export type Huella = { x: number; y: number; ancho: number; alto: number };

/** Lo que devuelven los tres endpoints que generan etiquetas para imprimir. */
type EtiquetasEmitidas = { etiquetas: Etiqueta[] };

export type CaminoPlanta = {
  id: number;
  nombre: string;
  tipo: string;
  x: number | null;
  y: number | null;
  ancho: number;
  largo: number;
  ubicado: boolean;
  /** Nave a la que pertenece; null cuando el camino está en el predio, fuera de las naves. */
  nave: number | null;
};

export type MuellePlanta = {
  id: string;
  orden: number;
  x: number | null;
  y: number | null;
  ancho: number;
  largo: number;
  ubicado: boolean;
  nave: number;
  /** Portón sobre el que abre; null mientras no se le asignó uno. */
  abertura: number | null;
};

/** Una pared interior, tal como la administra el editor de planta. */
export type ParedPlanta = {
  id: number;
  nave: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  espesor: number;
  /** null es «hasta el techo de la nave». */
  alto: number | null;
  material: string;
  largo: number;
  /** Cantidad, no la lista: mismo patrón que sectores/muelles/caminos de una nave. */
  aberturas: number;
};

/** Un portón, puerta, ventanal o paso, sobre una pared interior o sobre el perímetro. */
export type AberturaPlanta = {
  id: number;
  nave: number;
  /** Exactamente uno de `pared`/`lado` viene con valor: nunca los dos, nunca ninguno. */
  pared: number | null;
  lado: string | null;
  offset: number;
  ancho: number;
  alto: number;
  antepecho: number;
  tipo: string;
  nombre: string;
  /** Clave del muro que ocupa: la misma que arma `NaveMapa.muros`. */
  muro: string;
};

export type RackPlanta = {
  id: string;
  sector: string;
  numero: number;
  columnas: number;
  x: number | null;
  y: number | null;
  angulo: number;
  anchoPosicion: number;
  profundidad: number;
  niveles: number;
  altoNivel: number;
  largo: number;
  huella: Huella | null;
  ubicado: boolean;
  lugares: number;
  ocupados: number;
  /** Un rack común, la estación de fraccionamiento o una zona de piso: las dos últimas son siempre 1×1. */
  tipo: TipoRack;
};

export type SectorPlanta = {
  id: string;
  nombre: string;
  uso: string;
  orden: number;
  color: string | null;
  racks: number;
  /** Zona dibujada; sin ella, la caja que envuelve a sus racks. */
  area: Huella | null;
  dibujada: boolean;
  /** Racks del sector que quedaron fuera de la zona dibujada. */
  racksFuera: number;
  nave: number;
};

/** Una nave del predio, tal como la administra el editor de planta. */
export type NavePlanta = {
  id: number;
  nombre: string;
  x: number;
  y: number;
  ancho: number;
  largo: number;
  alto: number;
  orden: number;
  /** Cuánto tiene adentro: es lo que decide si se puede borrar. */
  sectores: number;
  muelles: number;
  caminos: number;
  paredes: number;
  aberturas: number;
  vacia: boolean;
  /** Racks de esta nave que se salen de ella. Se avisa, no se bloquea. */
  racksFuera: number;
};

export type Planta = {
  predio: { nombre: string; ancho: number; largo: number };
  naves: NavePlanta[];
  navePorDefecto: { anchoM: number; largoM: number; altoM: number };
  porDefecto: { anchoPosicionM: number; profundidadM: number; altoNivelM: number; niveles: number };
  limites: Record<string, { min: number; max: number }>;
  sectores: SectorPlanta[];
  muelles: MuellePlanta[];
  caminos: CaminoPlanta[];
  paredes: ParedPlanta[];
  aberturas: AberturaPlanta[];
  tiposDeCamino: { id: string; nombre: string }[];
  tiposDeAbertura: { id: string; nombre: string; anchoM: number; altoM: number; antepechoM: number }[];
  materialesDePared: { id: string; nombre: string }[];
  /** Medidas de una pared interior recién trazada. */
  paredPorDefecto: { espesorM: number; material: string };
  coloresDeSector: string[];
  racks: RackPlanta[];
  sinUbicar: number;
};

export type CambioRack = Partial<{
  x: number | null;
  y: number | null;
  angulo: number;
  columnas: number;
  niveles: number;
  anchoPosicion: number;
  profundidad: number;
  altoNivel: number;
  tipo: TipoRack;
}>;

export type ProblemaImport = { linea: number; pallet: string; motivo: string };

export type ResumenImport = {
  filas: number;
  validos: number;
  conProblemas: number;
  piezas: number;
  articulos: number;
  /** Materiales que esta carga da de alta en el catálogo. */
  articulosNuevos: number;
  /** De esos, los que nacen llamándose como su número de parte. */
  nuevosSinNombre: number;
  /** Filas sin número de parte: se identifican por el nombre, que se repite. */
  sinNumeroDeParte: number;
  posiciones: number;
  /** Los que entran sin posición: hay que ir a ubicarlos después. */
  sinUbicar: number;
  /** Los que salen enteros: no se fraccionan. */
  completos: number;
  sectores: string[];
  /** Los que vinieron sin código: el sistema los numera al confirmar. */
  sinCodigo: number;
  /** Los contenedores que nombra la planilla; los que no existen se crean. */
  contenedores: string[];
};

export type Importacion = {
  confirmado: boolean;
  resumen: ResumenImport;
  problemas: ProblemaImport[];
  muestra?: {
    linea: number;
    contenedor: string | null;
    pallet: string;
    articulo: string;
    lote: string | null;
    piezas: number;
    stockRdm: number | null;
    stockZf: number | null;
    posicion: string;
    nivel: number | null;
    tipo: TipoPallet;
  }[];
};

// ----------------------------------------------------- ubicaciones guardadas

/** Espejo de `Asignacion` en `backend/src/domain/ubicaciones.ts`. */
export type Asignacion = {
  pallet: string;
  /** El código que tenía en el CSV guardado; distinto de `pallet` cuando se renumeró. */
  antes: string;
  posicion: string;
  /** Null en una zona de piso. */
  nivel: number | null;
};

/** Espejo de `ResultadoUbicaciones`: lo que devuelve la vista previa o la aplicación. */
export type ResultadoUbicaciones = {
  asignar: Asignacion[];
  /** Ya estaban en su lugar. */
  yaEstan: number;
  sinPareja: string[];
  /** false en la vista previa. */
  aplicado: boolean;
  problemas: string[];
};

// ------------------------------------------------------- planilla de retrabajo

/** Espejo de `CambioPlanilla` en `backend/src/domain/planilla.ts`. */
export type CambioPlanilla = {
  clave: string;
  fila: number;
  /** El pallet del sistema; null en la vista previa de uno que todavía no existe. */
  pallet: string | null;
  parte: string;
  contenedor: string;
  detalle: string;
};

/** Espejo de `ResultadoSincronizacion`. */
export type ResultadoSincronizacion = {
  /** false en la vista previa. */
  aplicado: boolean;
  cuando: string;
  nuevos: CambioPlanilla[];
  salidas: CambioPlanilla[];
  cierres: CambioPlanilla[];
  /** Pallets que la hoja cambió de contenedor. */
  correcciones: CambioPlanilla[];
  revisar: string[];
  sinCambios: number;
};

/** Espejo de `EstadoPlanilla`. */
export type EstadoPlanilla = {
  /** false si faltan las variables de Google: la pantalla explica qué cargar. */
  configurada: boolean;
  hoja: string;
  cadaMinutos: number;
  ultima: ResultadoSincronizacion | null;
  error: string | null;
  /** La corrida automática pausada desde la pantalla, quién y cuándo. */
  pausada: boolean;
  pausadaPor: string | null;
  pausadaEn: string | null;
};

// ------------------------------------------------------------------ cliente

export const api = {
  // sesión
  configAuth: () => pedir<ConfigAuth>('/auth/config'),
  loginGoogle: (credential: string) =>
    pedir<{ token: string; usuario: Perfil }>('/auth/google', { method: 'POST', ...json({ credential }) }),
  loginDemo: () => pedir<{ token: string; usuario: Perfil }>('/auth/demo', { method: 'POST', ...json({}) }),
  yo: () => pedir<{ usuario: Perfil }>('/auth/yo'),
  /** Invalida el token en el servidor; borrarlo del navegador no alcanza. */
  salir: () => pedir<{ ok: true }>('/auth/salir', { method: 'POST' }),

  // planta (sólo admin)
  planta: () => pedir<Planta>('/planta'),
  guardarPredio: (body: { nombre: string }) =>
    pedir<{ ok: true }>('/planta', { method: 'PUT', ...json(body) }),
  crearNave: (body: { nombre?: string; x?: number; y?: number; ancho?: number; largo?: number; alto?: number }) =>
    pedir<{ id: number }>('/planta/naves', { method: 'POST', ...json(body) }),
  cambiarNave: (
    id: number,
    body: Partial<{ nombre: string; x: number; y: number; ancho: number; largo: number; alto: number }>,
  ) => pedir<{ ok: true }>(`/planta/naves/${id}`, { method: 'PATCH', ...json(body) }),
  borrarNave: (id: number) => pedir<{ ok: true }>(`/planta/naves/${id}`, { method: 'DELETE' }),
  cambiarSector: (
    id: string,
    body: {
      nombre?: string;
      uso?: string;
      color?: string;
      x?: number | null;
      y?: number | null;
      ancho?: number;
      largo?: number;
      nave?: number;
    },
  ) => pedir<{ ok: true }>(`/planta/sectores/${encodeURIComponent(id)}`, { method: 'PATCH', ...json(body) }),
  crearSector: (body: {
    id: string;
    nombre: string;
    uso: string;
    color?: string;
    x?: number;
    y?: number;
    ancho?: number;
    largo?: number;
    nave?: number;
  }) =>
    pedir<{ id: string }>('/planta/sectores', { method: 'POST', ...json(body) }),
  /**
   * Le cambia la letra a un sector. La base arrastra los códigos de sus racks
   * y de sus posiciones, y con ellos los pallets guardados y el historial: el
   * A1-01 pasa a llamarse E1-01 sin que nada se mueva de lugar.
   */
  renombrarSector: (id: string, nuevo: string) =>
    pedir<{ ok: true; id: string; racks: number }>(
      `/planta/sectores/${encodeURIComponent(id)}/renombrar`,
      { method: 'POST', ...json({ id: nuevo }) },
    ),
  borrarSector: (id: string) => pedir<{ ok: true }>(`/planta/sectores/${id}`, { method: 'DELETE' }),
  crearCamino: (body: {
    nombre: string;
    tipo: string;
    x?: number;
    y?: number;
    ancho?: number;
    largo?: number;
    /** `null` es a propósito: el camino que va en el predio, afuera de las naves. */
    nave?: number | null;
  }) =>
    pedir<{ ok: true; camino: CaminoPlanta }>('/planta/caminos', { method: 'POST', ...json(body) }),
  guardarCamino: (
    id: number,
    body: {
      nombre?: string;
      tipo?: string;
      x?: number | null;
      y?: number | null;
      ancho?: number;
      largo?: number;
      /** `null` saca el camino de su nave y lo deja en el predio. */
      nave?: number | null;
    },
  ) => pedir<{ ok: true; camino: CaminoPlanta }>(`/planta/caminos/${id}`, { method: 'PATCH', ...json(body) }),
  borrarCamino: (id: number) => pedir<{ ok: true }>(`/planta/caminos/${id}`, { method: 'DELETE' }),
  crearMuelle: (body: { id: string; ancho?: number; largo?: number; nave?: number }) =>
    pedir<{ ok: true; muelle: MuellePlanta }>('/planta/muelles', { method: 'POST', ...json(body) }),
  guardarMuelle: (
    id: string,
    body: {
      x?: number | null;
      y?: number | null;
      ancho?: number;
      largo?: number;
      nave?: number;
      /** null saca el muelle del portón que tenía asignado. */
      abertura?: number | null;
    },
  ) =>
    pedir<{ ok: true; muelle: MuellePlanta }>(`/planta/muelles/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      ...json(body),
    }),
  borrarMuelle: (id: string) =>
    pedir<{ ok: true }>(`/planta/muelles/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  // Con `x`/`y` el rack nace donde lo dibujaron; con `cantidad` nacen varios
  // paralelos, separados por `separacion` metros de pasillo. Dibujado, el
  // `largo` del trazo decide cuántas posiciones entran y pisa a `columnas`.
  crearRack: (body: {
    sector: string;
    numero: number;
    columnas: number;
    niveles: number;
    x?: number;
    y?: number;
    angulo?: number;
    largo?: number;
    cantidad?: number;
    separacion?: number;
    tipo?: TipoRack;
  }) => pedir<{ id: string; ids: string[]; columnas: number }>('/planta/racks', { method: 'POST', ...json(body) }),
  cambiarRack: (id: string, cambio: CambioRack) =>
    pedir<{ ok: true }>(`/planta/racks/${id}`, { method: 'PATCH', ...json(cambio) }),
  borrarRack: (id: string) => pedir<{ ok: true }>(`/planta/racks/${id}`, { method: 'DELETE' }),
  // El servidor devuelve la fila completa en el POST y en el PATCH, no un
  // { id }: así el cliente no tiene que volver a pedir la planta para
  // dibujar lo que acaba de crear o mover.
  crearPared: (body: {
    nave: number;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    espesor?: number;
    alto?: number | null;
    material?: string;
  }) => pedir<ParedPlanta>('/planta/paredes', { method: 'POST', ...json(body) }),
  cambiarPared: (
    id: number,
    body: Partial<{
      nave: number;
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      espesor: number;
      alto: number | null;
      material: string;
    }>,
  ) => pedir<ParedPlanta>(`/planta/paredes/${id}`, { method: 'PATCH', ...json(body) }),
  borrarPared: (id: number) => pedir<{ ok: true }>(`/planta/paredes/${id}`, { method: 'DELETE' }),
  crearAbertura: (body: {
    /** Sólo hace falta si va sobre un lado: si va sobre una pared, la nave la pone la pared. */
    nave?: number;
    // Exactamente uno de los dos tiene que resolver a un valor: el servidor
    // trata `null` igual que si no viniera (los dos cuentan como «no vino»),
    // así que se puede mandar el que no se usa en `null` sin que rechace el
    // pedido — es justo lo que le permite a `cambiarAbertura` mudar una
    // abertura de una pared al perímetro (o viceversa) en el mismo PATCH.
    pared?: number | null;
    lado?: string | null;
    offset: number;
    ancho?: number;
    alto?: number;
    antepecho?: number;
    tipo: string;
    nombre?: string;
  }) => pedir<AberturaPlanta>('/planta/aberturas', { method: 'POST', ...json(body) }),
  cambiarAbertura: (
    id: number,
    body: Partial<{
      // null en cualquiera de los dos es a propósito: es lo que le permite
      // al cliente mudar una abertura de una pared al perímetro (o viceversa)
      // en el mismo PATCH.
      pared: number | null;
      lado: string | null;
      offset: number;
      ancho: number;
      alto: number;
      antepecho: number;
      tipo: string;
      nombre: string;
    }>,
  ) => pedir<AberturaPlanta>(`/planta/aberturas/${id}`, { method: 'PATCH', ...json(body) }),
  borrarAbertura: (id: number) => pedir<{ ok: true }>(`/planta/aberturas/${id}`, { method: 'DELETE' }),

  // usuarios (sólo admin)
  usuarios: () => pedir<Usuarios>('/usuarios'),
  // ---------------------------------------------------------------- artículos
  catalogoArticulos: () => pedir<CatalogoArticulos>('/articulos/catalogo'),
  importarProductos: (archivo: File, confirmar: boolean) => {
    const fd = new FormData();
    fd.append('archivo', archivo);
    return pedir<ImportacionProductos>(`/articulos/importar?confirmar=${confirmar}`, { method: 'POST', body: fd });
  },
    descargarPlantillaProductos: () =>
    descargar('/articulos/plantilla.csv', 'plantilla-productos.csv', 'No pudimos generar la plantilla.'),
  guardarArticulo: (
    codigo: string,
    body: { descripcion?: string; proveedor?: string; detalle?: string; maxPorCapacho?: number | null },
  ) =>
    pedir<{ codigo: string; descripcion: string; proveedor: string; detalle: string; maxPorCapacho: number | null }>(
      `/articulos/${encodeURIComponent(codigo)}`,
      { method: 'PATCH', ...json(body) },
    ),
  /** Le cambia el código a un material; la base arrastra todo lo que lo referencia. */
  renombrarArticulo: (codigo: string, body: { codigo: string; descripcion?: string }) =>
    pedir<{ ok: true; articulo: { codigo: string; descripcion: string; proveedor: string; detalle: string } }>(
      `/articulos/${encodeURIComponent(codigo)}/renombrar`,
      { method: 'POST', ...json(body) },
    ),
  proveedorEnLote: (codigos: string[], proveedor: string) =>
    pedir<{ ok: true; cambiados: number }>('/articulos/proveedor', {
      method: 'POST',
      ...json({ codigos, proveedor }),
    }),

  // ----------------------------------------------------------------- remitos
  /** Con `remito`, sus propias órdenes también se ofrecen: es para editarlo. */
  /** Pasa a otro punto de venta (0009 -> 0010); el nuevo numera desde 1. Sólo admin. */
  cambiarPuntoDeVenta: (puntoDeVenta: string) =>
    pedir<{ puntoDeVenta: string }>('/remitos/punto-de-venta', { method: 'PUT', ...json({ puntoDeVenta }) }),
  remitoNuevo: (remito?: string) =>
    pedir<NuevoRemito>(`/remitos/nuevo${remito ? `?remito=${encodeURIComponent(remito)}` : ''}`),
  remitos: (q: string) => pedir<RemitoEnLista[]>(`/remitos?q=${encodeURIComponent(q)}`),
  remito: (id: string) => pedir<Remito>(`/remitos/${encodeURIComponent(id)}`),
  emitirRemito: (body: {
    fecha: string;
    transporte: string;
    transporteCuit: string;
    transporteDomicilio: string;
    observaciones: string;
    facturas: string;
    /** Vacío: el siguiente automático. */
    numero: string;
    lineas: LineaRemito[];
  }) => pedir<{ id: string }>('/remitos', { method: 'POST', ...json(body) }),
  /** Corrige uno ya emitido; el número no cambia y queda en su historial. */
  editarRemito: (
    id: string,
    body: {
      fecha: string;
      transporte: string;
      transporteCuit: string;
      transporteDomicilio: string;
      observaciones: string;
      facturas: string;
      lineas: LineaRemito[];
    },
  ) => pedir<{ id: string; cambios: string[] }>(`/remitos/${encodeURIComponent(id)}`, { method: 'PUT', ...json(body) }),
  /** Lo da de baja: queda guardado con su número, marcado como anulado. */
  darDeBajaRemito: (id: string, motivo: string) =>
    pedir<{ id: string }>(`/remitos/${encodeURIComponent(id)}/baja`, { method: 'POST', ...json({ motivo }) }),

  // ------------------------------------------------------------ mantenimiento
  limpieza: () => pedir<Limpieza>('/mantenimiento/limpieza'),
  vaciar: (que: string[], confirmacion: string) =>
    pedir<{ ok: true; vaciado: { id: string; nombre: string; filas: number }[] }>('/mantenimiento/limpieza', {
      method: 'POST',
      ...json({ que, confirmacion }),
    }),

  // ------------------------------------------------------------ carga manual
  catalogoManual: () => pedir<CatalogoManual>('/manual/catalogo'),
  crearProveedorManual: (nombre: string, numero: string) =>
    pedir<ProveedorManual>('/manual/proveedores', { method: 'POST', ...json({ nombre, numero }) }),
  crearMaterialManual: (codigo: string, nombre: string, proveedorId: number | null) =>
    pedir<MaterialManual>('/manual/materiales', { method: 'POST', ...json({ codigo, nombre, proveedorId }) }),
  anotarUsadosManual: (valores: Record<string, string>) =>
    pedir<{ anotados: number }>('/manual/usados', { method: 'POST', ...json({ valores }) }),

  // ---------------------------------------------------------------- etiquetas
  catalogoEtiquetas: () => pedir<CatalogoEtiquetas>('/etiquetas/catalogo'),
  etiquetas: (uso?: UsoEtiqueta) =>
    pedir<{ etiquetas: EtiquetaPlantilla[] }>(`/etiquetas${uso ? `?uso=${uso}` : ''}`),
  crearEtiqueta: (body: {
    nombre: string;
    descripcion?: string;
    ancho: number;
    alto: number;
    uso?: UsoEtiqueta;
    orientacion?: Orientacion;
  }) =>
    pedir<EtiquetaPlantilla>('/etiquetas', { method: 'POST', ...json(body) }),
  guardarEtiqueta: (
    id: number,
    body: {
      nombre?: string;
      descripcion?: string;
      ancho?: number;
      alto?: number;
      uso?: UsoEtiqueta;
      orientacion?: Orientacion;
      plantilla?: PlantillaEtiqueta;
      activa?: boolean;
    },
  ) => pedir<EtiquetaPlantilla>(`/etiquetas/${id}`, { method: 'PATCH', ...json(body) }),
  duplicarEtiqueta: (id: number) => pedir<EtiquetaPlantilla>(`/etiquetas/${id}/duplicar`, { method: 'POST' }),
  etiquetaPredeterminada: (id: number) =>
    pedir<{ ok: true }>(`/etiquetas/${id}/predeterminada`, { method: 'POST' }),
  borrarEtiqueta: (id: number) => pedir<{ ok: true }>(`/etiquetas/${id}`, { method: 'DELETE' }),

  crearUsuario: (body: { mail: string; nombre: string; rol: Rol }) =>
    pedir<{ id: number; mail: string; rolEtiqueta: string }>('/usuarios', { method: 'POST', ...json(body) }),
  cambiarRol: (id: number, rol: Rol) =>
    pedir<{ ok: true; rolEtiqueta: string }>(`/usuarios/${id}`, { method: 'PATCH', ...json({ rol }) }),
  quitarUsuario: (id: number) => pedir<{ ok: true }>(`/usuarios/${id}`, { method: 'DELETE' }),
  devolverAcceso: (id: number) => pedir<{ ok: true }>(`/usuarios/${id}/desbloquear`, { method: 'POST' }),

  // portal del cliente
  dashboard: () => pedir<Dashboard>('/dashboard'),
  /**
   * Marcar un pallet como que sale entero, o volverlo a fraccionamiento. El
   * servidor lo rechaza si el pallet ya tiene capachos encima.
   */
  cambiarTipoPallet: (id: string, tipo: TipoPallet) =>
    pedir<{ ok: true; tipo: TipoPallet }>(`/pallets/${encodeURIComponent(id)}/tipo`, {
      method: 'PATCH',
      ...json({ tipo }),
    }),
  stock: (q: string, estado: string) =>
    pedir<Stock>(`/stock?q=${encodeURIComponent(q)}&estado=${encodeURIComponent(estado)}`),
  articulos: () => pedir<ArticuloDisponible[]>('/articulos'),
  contenedores: () => pedir<Contenedor[]>('/contenedores'),
  ordenes: () => pedir<Orden[]>('/ordenes'),
  /**
   * Manda un pedido: una orden, con un renglón por artículo. En capachos cada
   * renglón manda `piezas` —el total, lo único que escribe quien pide— y el
   * corte lo hace el servidor con el máximo por capacho del material. En
   * pallets completos manda `capachos`, que son cuántos pallets.
   */
  crearOrden: (body: {
    lineas: { articulo: string; piezas?: number; capachos?: number }[];
    unidad: Unidad;
    observaciones: string;
    entregaEstimada: string;
  }) =>
    pedir<{ id: string; detalle: string; totalPiezas: number | null; lineas: RenglonOrden[] }>('/ordenes', {
      method: 'POST',
      ...json(body),
    }),
  /** Le agrega renglones a una orden ya pedida, igual que al crearla. */
  agregarAOrden: (id: string, lineas: { articulo: string; piezas?: number; capachos?: number }[]) =>
    pedir<{ id: string; detalle: string }>(`/ordenes/${encodeURIComponent(id)}/renglones`, {
      method: 'POST',
      ...json({ lineas }),
    }),
  /** El total nuevo de un artículo de la orden, en piezas (o pallets). Cero lo quita. */
  cambiarCantidad: (id: string, articulo: string, cantidad: number) =>
    pedir<{ id: string; detalle: string }>(`/ordenes/${encodeURIComponent(id)}/renglones`, {
      method: 'PATCH',
      ...json({ articulo, cantidad }),
    }),
  /** Los capachos armados sin orden, con los pedidos de otras órdenes en que entran. */
  armadosLibres: () =>
    pedir<
      {
        id: string;
        articulo: string | null;
        descripcion: string | null;
        piezas: number;
        pallet: string | null;
        ubicacion: string | null;
        destinos: { orden: string; capacho: string; piezas: number }[];
      }[]
    >('/fraccionamiento/armados'),
  /** Saca un capacho aceptado de su orden: queda armado para otra y la orden lo vuelve a pedir. */
  sacarCapacho: (id: string) =>
    pedir<{ ok: true; capacho: string; pedido: string }>(`/fraccionamiento/capachos/${encodeURIComponent(id)}/sacar`, {
      method: 'POST',
    }),
  /** Sólo mientras la orden sigue «Pendiente»: ver `Orden.estado`. */
  cancelarOrden: (id: string) =>
    pedir<{ ok: true }>(`/ordenes/${encodeURIComponent(id)}/cancelar`, { method: 'POST' }),

  // recepción
  recepcion: (contenedor: string) => pedir<Recepcion>(`/recepcion/${encodeURIComponent(contenedor)}`),
  escanear: (contenedor: string, codigo: string) =>
    pedir<Lectura>(`/recepcion/${encodeURIComponent(contenedor)}/scan`, { method: 'POST', ...json({ codigo }) }),
  ajustarLinea: (lineaId: number, delta: number) =>
    pedir<Recepcion>(`/recepcion/lineas/${lineaId}`, { method: 'PATCH', ...json({ delta }) }),
  confirmarIngreso: (contenedor: string) =>
    pedir<Recepcion & { nuevoEstado: string; ingresados: number }>(`/recepcion/${encodeURIComponent(contenedor)}/confirmar`, {
      method: 'POST',
    }),
  subirAdjunto: (contenedor: string, archivo: File) => {
    const fd = new FormData();
    fd.append('archivo', archivo);
    return pedir<Adjunto>(`/recepcion/${encodeURIComponent(contenedor)}/adjuntos`, { method: 'POST', body: fd });
  },
  borrarAdjunto: (id: number) => pedir<{ ok: true }>(`/recepcion/adjuntos/${id}`, { method: 'DELETE' }),

  // etiquetas de pallet
  palletsDeContenedor: (contenedor: string) =>
    pedir<PalletEtiqueta[]>(`/etiquetas-pallet?contenedor=${encodeURIComponent(contenedor)}`),
  /** Los pallets completos que siguen en el depósito, de cualquier contenedor. */
  palletsCompletos: () => pedir<PalletEtiqueta[]>('/etiquetas-pallet?completos=true'),
  palletsPorCodigo: (codigos: string[]) =>
    pedir<{ pallets: PalletEtiqueta[]; desconocidos: string[] }>(
      `/etiquetas-pallet?codigos=${encodeURIComponent(codigos.join(','))}`,
    ),

  // resumen para administración, y lo que se le cobra a ZF
  resumen: (periodo: Periodo, dia?: string) =>
    pedir<ResumenDiario>(`/resumen?periodo=${periodo}${dia ? `&dia=${encodeURIComponent(dia)}` : ''}`),
  tarifas: () => pedir<Tarifas>('/tarifas'),
  historialTarifas: () => pedir<HistorialTarifas>('/tarifas/historial'),
  guardarTarifas: (t: Tarifas) => pedir<Tarifas>('/tarifas', { method: 'PUT', ...json(t) }),
  simularFacturacion: (tarifas: Tarifas, cantidades: CantidadesFacturables) =>
    pedir<Cuenta>('/facturacion/simular', { method: 'POST', ...json({ tarifas, cantidades }) }),
    descargarResumenCsv: (periodo: Periodo, dia: string) =>
    descargar(
      `/resumen/dia.csv?periodo=${periodo}&dia=${encodeURIComponent(dia)}`,
      `resumen-${periodo}-${dia}.csv`,
      'No pudimos generar el resumen.',
    ),

  // el contenedor: la puerta de entrada de la mercadería
  contenedorNuevo: () => pedir<NuevoContenedor>('/contenedores/nuevo'),
  fichaContenedor: (id: string) => pedir<FichaContenedor>(`/contenedores/${encodeURIComponent(id)}/ficha`),
  /** Un pallet nuevo armado de cero en el contenedor; el artículo puede no estar en el maestro. */
  crearPalletEnContenedor: (
    id: string,
    body: { articulo: string; descripcion: string; lote: string; numeroParte: string; piezas: number; tipo: TipoPallet },
  ) => pedir<{ pallet: string }>(`/contenedores/${encodeURIComponent(id)}/pallets`, { method: 'POST', ...json(body) }),
  /** Lo pasa de «En tránsito» a «En descarga» sin esperar a que se ubique un pallet. */
  ponerEnDescarga: (id: string) =>
    pedir<{ ok: true; id: string }>(`/contenedores/${encodeURIComponent(id)}/descarga`, { method: 'POST' }),
  /** Corrige los datos de un contenedor ya cargado. Sólo administración. */
  editarContenedor: (
    id: string,
    body: Papeles & { fecha?: string; muelle?: string | null; numero?: number | null; codigo?: string },
  ) => pedir<{ ok: true; id: string }>(`/contenedores/${encodeURIComponent(id)}`, { method: 'PATCH', ...json(body) }),
  /** Borra el contenedor con sus pallets. Sólo administración. */
  borrarContenedor: (id: string) =>
    pedir<{ ok: true; id: string; pallets: number }>(`/contenedores/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  crearContenedor: (body: {
    id: string;
    fecha: string;
    muelle: string | null;
    lineas: LineaDeclarada[];
  } & Papeles) =>
    pedir<{ id: string; lineas: number; piezas: number; pallets: string[] }>('/contenedores', {
      method: 'POST',
      ...json(body),
    }),

  // armado de pallets
  armado: () => pedir<Armado>('/armado'),
  /**
   * Arma pallets, o le suma un artículo a uno que ya está armado.
   *
   * Sin `pallet` arma `cantidad` pallets nuevos, todos iguales: el código lo
   * pone el sistema siguiendo la numeración que ya hay. Con `pallet`, le
   * agrega un renglón a ése, que es como se arma uno mezclado.
   */
  agregarLinea: (body: {
    pallet?: string;
    stockZfId: number;
    piezas: number;
    cantidad?: number;
    tipo?: TipoPallet;
    contenedor?: string;
  }) =>
    pedir<{ pallets: string[]; nuevo: boolean; tipo: TipoPallet; piezas: number; cantidad: number }>(
      '/armado/lineas',
      { method: 'POST', ...json(body) },
    ),
  quitarLinea: (id: number) =>
    pedir<{ ok: true; pallet: string; palletBorrado: boolean }>(`/armado/lineas/${id}`, { method: 'DELETE' }),
  itemsDePallet: (pallet: string) =>
    pedir<{ pallet: string; items: LineaDePallet[] }>(`/pallets/${encodeURIComponent(pallet)}/items`),
  /** Borra el pallet y deja la foto en `pallets-eliminados`. Sólo administración. */
  /** Admin: fija las piezas que tiene arriba un artículo del pallet. Con motivo. */
  corregirPiezas: (pallet: string, itemId: number, piezas: number, motivo: string) =>
    pedir<{ ok: true }>(`/pallets/${encodeURIComponent(pallet)}/items/${itemId}`, {
      method: 'PATCH',
      ...json({ piezas, motivo }),
    }),
  /** Un pallet ya eliminado sale también de lo declarado: el contenedor deja de esperarlo. */
  quitarDeclaradoDeEliminado: (id: number) =>
    pedir<{ ok: true; piezas: number }>(`/pallets-eliminados/${id}/quitar-declarado`, { method: 'POST' }),
  /** Vuelve a poner en el sistema un pallet eliminado, como estaba. `ubicacion` null: volvió sin ubicar. */
  restablecerEliminado: (id: number) =>
    pedir<{ ok: true; pallet: string; piezas: number; ubicacion: string | null }>(`/pallets-eliminados/${id}/restablecer`, {
      method: 'POST',
    }),
  /** Con `quitarDeclarado` sale también de lo declarado del contenedor (ficha del contenedor). */
  eliminarPallet: (id: string, motivo: string, quitarDeclarado = false) =>
    pedir<PalletEliminado>(`/pallets/${encodeURIComponent(id)}`, { method: 'DELETE', ...json({ motivo, quitarDeclarado }) }),
  palletsEliminados: (q = '') => pedir<PalletEliminado[]>(`/pallets-eliminados?q=${encodeURIComponent(q)}`),

  // fraccionamiento
  fraccionamiento: (pallet: string) => pedir<Fraccionamiento>(`/fraccionamiento/${encodeURIComponent(pallet)}`),
  ordenEnMesa: (orden: string) =>
    pedir<OrdenDeFraccionamiento>(`/fraccionamiento/ordenes/${encodeURIComponent(orden)}`),
  /** `capachos` son cuáles imprimir; vacío es todos los aceptados de la orden. */
  etiquetasDeOrden: (orden: string, grupos: DatosPorGrupo, datos: Record<string, string> = {}, capachos: string[] = []) =>
    pedir<EtiquetasEmitidas>(`/fraccionamiento/ordenes/${encodeURIComponent(orden)}/etiquetas`, {
      method: 'POST',
      ...json({ grupos, datos, capachos }),
    }),
  /** Borra lo que sigue pedido sin pallet y deja escrito en la orden qué no salió. */
  /** `guardar`: capachos aceptados que quedan armados para otra orden en vez de entregarse. */
  cerrarOrden: (orden: string, guardar: string[] = []) =>
    pedir<{ ok: true; faltante: string; guardados: string[]; cancelada: boolean }>(
      `/fraccionamiento/ordenes/${encodeURIComponent(orden)}/cerrar`,
      { method: 'POST', ...json({ guardar }) },
    ),
  /** Acepta el pedido con un capacho que quedó armado de otra orden. */
  // Y lo entrega de una: ya está armado y etiquetado, y dejarlo aceptado lo
  // ofrece para imprimirle otra etiqueta sin querer.
  usarArmado: async (id: string, armado: string) => {
    await pedir<{ ok: true; capacho: string; pallet: string | null; estado: EstadoCapacho }>(
      `/fraccionamiento/capachos/${encodeURIComponent(id)}/estado`,
      { method: 'PATCH', ...json({ estado: 'Aceptado', armado }) },
    );
    try {
      return await api.moverCapacho(armado, 'Entregado');
    } catch (err) {
      throw new Error(`${armado} ya está en la orden, pero no se pudo entregar: ${err instanceof Error ? err.message : String(err)}`);
    }
  },
  historialDePallet: (pallet: string) =>
    pedir<HistorialDePallet>(`/pallets/${encodeURIComponent(pallet)}/historial`),
  agregarCapacho: (pallet: string, piezas: number, item?: number) =>
    pedir<{ id: string; piezas: number; saldo: number; item: number; articulo: string }>(
      `/fraccionamiento/${encodeURIComponent(pallet)}/capachos`,
      { method: 'POST', ...json({ piezas, item }) },
    ),
  quitarCapacho: (id: string) =>
    pedir<{ ok: true; pallet: string }>(`/fraccionamiento/capachos/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  /** Marca el capacho como armado desde `pallet`. No cambia estado ni stock. */
  armarCapacho: (id: string, pallet: string) =>
    pedir<{
      ok: true;
      capacho: string;
      orden: string | null;
      piezas: number;
      armadoEn: string;
      armadoPor: string;
      yaArmado: boolean;
    }>(`/fraccionamiento/capachos/${encodeURIComponent(id)}/armado`, { method: 'POST', ...json({ pallet }) }),
  /**
   * Mueve un capacho de estado. `item` sólo hace falta al aceptar un pedido de
   * ZF, que es cuando se decide de qué línea de qué pallet sale.
   */
  moverCapacho: (id: string, estado: EstadoCapacho, item?: number) =>
    pedir<{ ok: true; capacho: string; pallet: string | null; estado: EstadoCapacho }>(
      `/fraccionamiento/capachos/${encodeURIComponent(id)}/estado`,
      { method: 'PATCH', ...json({ estado, item }) },
    ),
  /** Qué hay que cargar a mano para las etiquetas de los pallets de un contenedor. */
  pedidosDeRecepcion: (contenedor: string) =>
    pedir<PedidosDeEtiqueta>(`/recepcion/${encodeURIComponent(contenedor)}/pedidos`),
  etiquetasDeRecepcion: (contenedor: string, datos: Record<string, string> = {}) =>
    pedir<EtiquetasEmitidas>(`/recepcion/${encodeURIComponent(contenedor)}/etiquetas`, {
      method: 'POST',
      ...json({ datos }),
    }),

  /** Qué hay que cargar a mano para poder emitir la etiqueta de este pallet. */
  pedidosDeEtiqueta: (pallet: string) =>
    pedir<PedidosDeEtiqueta>(`/fraccionamiento/${encodeURIComponent(pallet)}/pedidos`),
  /** `capachos` son cuáles imprimir; vacío es todos. */
  generarEtiquetas: (pallet: string, grupos: DatosPorGrupo, datos: Record<string, string> = {}, capachos: string[] = []) =>
    pedir<EtiquetasEmitidas>(`/fraccionamiento/${encodeURIComponent(pallet)}/etiquetas`, {
      method: 'POST',
      ...json({ grupos, datos, capachos }),
    }),

  /** Resuelve una etiqueta leída con la cámara o el lector. */
  buscar: (codigo: string) => pedir<Hallazgo>(`/buscar?codigo=${encodeURIComponent(codigo)}`),

  // mapa
  mapa: (q: string) => pedir<Mapa>(`/mapa?q=${encodeURIComponent(q)}`),
  movimientos: (codigo: string) => pedir<Movimiento[]>(`/mapa/posiciones/${encodeURIComponent(codigo)}/movimientos`),
  /**
   * Con `origen`, el servidor rechaza (409) si el pallet ya no está ahí. El
   * `motivo` queda en el detalle del movimiento.
   */
  asignarPosicion: (
    codigo: string,
    pallet: string,
    /** Null en una zona de piso, que no tiene niveles. */
    nivel: number | null,
    origen?: { posicion: string; nivel: number | null },
    motivo?: string,
  ) =>
    pedir<{ ok: true }>(`/mapa/posiciones/${encodeURIComponent(codigo)}/asignar`, {
      method: 'POST',
      ...json({ pallet, nivel, origen, motivo }),
    }),
  /** Lo saca de su posición y lo devuelve a la cola de sin ubicar. */
  desubicar: (pallet: string) =>
    pedir<{ ok: true }>(`/mapa/pallets/${encodeURIComponent(pallet)}/desubicar`, { method: 'POST' }),
  /** Los interruptores de la aplicación que maneja el admin desde Mantenimiento. */
  ajustes: () => pedir<Ajustes>('/ajustes'),
  cambiarAjustes: (cambio: { sacarEnteros: boolean }) => pedir<Ajustes>('/ajustes', { method: 'PUT', ...json(cambio) }),
  cambiarRollo: (cantidad: number) => pedir<Ajustes>('/ajustes/rollo', { method: 'PUT', ...json({ cantidad }) }),
  usarRollo: (cantidad: number) =>
    pedir<{ rolloEtiquetas: number | null }>('/ajustes/rollo/usar', { method: 'POST', ...json({ cantidad }) }),
  /** Saca varios pallets de sus posiciones de una vez: todos o ninguno. */
  desubicarVarios: (pallets: string[]) =>
    pedir<{ ok: true; pallets: string[] }>('/mapa/desubicar', { method: 'POST', ...json({ pallets }) }),

  // estadías
  estadias: () => pedir<Estadias>('/estadias'),
  /** Descarga la permanencia de hoy pasando por fetch para poder mandar el token. */
    async descargarPermanencia(): Promise<string> {
    const nombre = `permanencia-${hoyISO()}.csv`;
    await descargar('/estadias/permanencia.csv', nombre, 'No pudimos generar la planilla de permanencia.');
    return nombre;
  },

  // carga inicial de inventario
  importarInventario: (archivo: File, confirmar: boolean) => {
    const fd = new FormData();
    fd.append('archivo', archivo);
    return pedir<Importacion>(`/inventario/importar?confirmar=${confirmar}`, { method: 'POST', body: fd });
  },
  /** Descarga la plantilla pasando por fetch para poder mandar el token. */
    descargarPlantilla: () =>
    descargar('/inventario/plantilla.csv', 'plantilla-inventario.csv', 'No pudimos generar la plantilla.'),

  // ubicaciones guardadas: se bajan antes de vaciar el stock y se restauran después
  /** Descarga el CSV con dónde está cada pallet, para guardarlo antes de vaciar. */
    descargarUbicaciones: () =>
    descargar('/inventario/ubicaciones.csv', `ubicaciones-${hoyISO()}.csv`, 'No pudimos generar el CSV de ubicaciones.'),
  previaUbicaciones: (archivo: File) => {
    const fd = new FormData();
    fd.append('archivo', archivo);
    return pedir<ResultadoUbicaciones>('/inventario/ubicaciones?confirmar=false', { method: 'POST', body: fd });
  },
  restaurarUbicaciones: (archivo: File) => {
    const fd = new FormData();
    fd.append('archivo', archivo);
    return pedir<ResultadoUbicaciones>('/inventario/ubicaciones?confirmar=true', { method: 'POST', body: fd });
  },

  // planilla de retrabajo (sólo admin)
  planilla: () => pedir<EstadoPlanilla>('/planilla'),
  previaPlanilla: () => pedir<ResultadoSincronizacion>('/planilla/sincronizar?confirmar=false', { method: 'POST' }),
  sincronizarPlanilla: () => pedir<ResultadoSincronizacion>('/planilla/sincronizar?confirmar=true', { method: 'POST' }),
  pausarPlanilla: (pausada: boolean) =>
    pedir<{ pausada: boolean; pausadaPor: string | null; pausadaEn: string | null }>('/planilla/pausa', {
      method: 'PUT',
      ...json({ pausada }),
    }),

  // archivos compartidos con el cliente
  archivos: () => pedir<Buzon>('/archivos'),
  subirArchivo: (archivo: File, nota: string) => {
    const fd = new FormData();
    // La nota va antes del archivo: el servidor lee los campos que ya
    // llegaron cuando toma el archivo, no los que vienen después.
    fd.append('nota', nota);
    fd.append('archivo', archivo);
    return pedir<{ id: number; nombre: string; peso: string }>('/archivos', { method: 'POST', body: fd });
  },
  /** Como la plantilla: por fetch, porque la sesión va en una cabecera. */
    descargarArchivo: (id: number, nombre: string) =>
    descargar(`/archivos/${id}`, nombre, 'No pudimos descargar el archivo.'),
  borrarArchivo: (id: number) => pedir<{ ok: true }>(`/archivos/${id}`, { method: 'DELETE' }),
  enlacesEntrega: () => pedir<{ enlaces: EnlaceEntrega[] }>('/archivos/enlaces'),
  crearEnlace: (rotulo: string, dias: number) =>
    pedir<{ token: string; enlace: EnlaceEntrega }>('/archivos/enlaces', { method: 'POST', ...json({ rotulo, dias }) }),
  revocarEnlace: (id: number) => pedir<{ ok: true }>(`/archivos/enlaces/${id}`, { method: 'DELETE' }),

  // pedidos por mail
  correo: () => pedir<EstadoCorreo>('/correo'),
  correoHtml: (id: number) => pedir<{ html: string }>(`/correo/${id}/html`),
  conectarCorreo: (code: string) => pedir<EstadoCorreo>('/correo/conectar', { method: 'POST', ...json({ code }) }),
  desconectarCorreo: () => pedir<EstadoCorreo>('/correo/conectar', { method: 'DELETE' }),
  revisarCorreo: () => pedir<{ nuevos: number }>('/correo/revisar', { method: 'POST' }),
  crearOtDesdeMail: (
    id: number,
    cuerpo: { lineas: { articulo: string; piezas: number }[]; entregaEstimada: string | null; observaciones?: string },
  ) => pedir<{ id: string; detalle: string }>(`/correo/${id}/crear`, { method: 'POST', ...json(cuerpo) }),
  releerMail: (id: number) =>
    pedir<{ lineas: { articulo: string; piezas: number }[]; entrega: string | null; error: string | null }>(
      `/correo/${id}/releer`,
      { method: 'POST' },
    ),
  descartarMail: (id: number) => pedir<{ ok: true }>(`/correo/${id}/descartar`, { method: 'POST' }),

  // notificaciones
  notificaciones: () => pedir<Notificaciones>('/notificaciones'),
  leerNotificacion: (id: number) => pedir<{ ok: true }>(`/notificaciones/${id}/leer`, { method: 'POST' }),
  leerTodas: () => pedir<{ ok: true }>('/notificaciones/leer-todas', { method: 'POST' }),
};

// ------------------------------------------ pedidos por mail

export type EstadoPedidoMail = 'pendiente' | 'creada' | 'descartada' | 'ilegible';
export type RenglonPedidoMail = {
  articulo: string;
  piezas: number;
  descripcion: string | null;
  maxPorCapacho: number | null;
  existe: boolean;
  /** Piezas libres para fraccionar, menos lo que ya pidieron otras OT abiertas. */
  disponible: number;
  /** Lo que ya pidieron otras OT abiertas y todavía no tiene pallet. */
  comprometidas: number;
  /** Piezas en pallets completos sin tocar: no cuentan, pero se pueden abrir. */
  enCompletos: number;
  alcanza: boolean;
};
export type PedidoMail = {
  id: number;
  de: string;
  asunto: string;
  recibidoEn: string;
  estado: EstadoPedidoMail;
  error: string | null;
  entregaEstimada: string | null;
  lineas: RenglonPedidoMail[];
  /** Algún renglón pide más de lo disponible: lo tiene que decidir el encargado. */
  faltaStock: boolean;
  ordenId: string | null;
  resueltoPor: string | null;
  resueltoEn: string | null;
};
export type EstadoCorreo = {
  configurado: boolean;
  googleClientId: string | null;
  scope: string;
  conectada: { mail: string; conectadaPor: string; conectadaEn: string } | null;
  puedeConectar: boolean;
  ultimaRevision: string | null;
  error: string | null;
  pedidos: PedidoMail[];
};

// ------------------------------------------ archivos compartidos con el cliente

export type ArchivoCompartido = {
  id: number;
  nombre: string;
  peso: string;
  nota: string;
  /** El mail de quien lo subió, o el rótulo del enlace si entró sin sesión. */
  subidoPor: string;
  subidoEn: string;
  porEnlace: boolean;
};

export type Buzon = { archivos: ArchivoCompartido[]; extensiones: string[] };

export type EstadoEnlace = 'vigente' | 'revocado' | 'vencido' | 'agotado';

export type EnlaceEntrega = {
  id: number;
  rotulo: string;
  estado: EstadoEnlace;
  vence: string;
  usados: number;
  maxArchivos: number;
  restantes: number;
  archivos: number;
  creadoPor: string;
  creadoEn: string;
};

export type Entrega = {
  rotulo: string;
  vence: string;
  restantes: number;
  extensiones: string[];
  entregados: { nombre: string; peso: string; nota: string; subidoEn: string }[];
};

/** La cabecera con la que viaja el token de un enlace de entrega. */
const CABECERA_ENTREGA = 'X-Entrega-Token';

/**
 * Pedido con un enlace de entrega en vez de una sesión.
 *
 * No pasa por `pedir()` a propósito: no manda `Authorization`, y un 401 acá no
 * significa «tu sesión venció» —no hay sesión— sino que el enlace no sirve, así
 * que no hay que borrar ningún token ni mandar a nadie al login.
 */
async function pedirEntrega<T>(token: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set(CABECERA_ENTREGA, token);
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json');

  let res: Response;
  try {
    res = await fetch(`${BASE}/entrega`, { ...init, headers });
  } catch {
    throw new ApiError('No pudimos contactar al servidor del depósito.', 0);
  }

  const texto = await res.text();
  const datos = texto ? (JSON.parse(texto) as unknown) : null;

  if (!res.ok) {
    const mensaje =
      datos && typeof datos === 'object' && 'error' in datos
        ? String((datos as { error: unknown }).error)
        : `Error ${res.status}`;
    throw new ApiError(mensaje, res.status);
  }

  return datos as T;
}

export const apiEntrega = {
  ver: (token: string) => pedirEntrega<Entrega>(token),
  subir: (token: string, archivo: File, nota: string) => {
    const fd = new FormData();
    fd.append('nota', nota);
    fd.append('archivo', archivo);
    return pedirEntrega<{ nombre: string; peso: string; restantes: number }>(token, { method: 'POST', body: fd });
  },
};
